/**
 * AI Assistant Prompts Component
 *
 * Pre-built quick prompts for each onboarding step
 * Provides easy access to AI suggestions
 */

import React from 'react';
import {
  Box,
  Button,
  Chip,
  Typography,
  Paper,
  Stack,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  AutoAwesome as SparkleIcon,
  Lightbulb as IdeaIcon
} from '@mui/icons-material';

const AIAssistantPrompts = ({ step, onPromptClick, data, disabled = false }) => {
  const theme = useTheme();

  const prompts = getPromptsForStep(step, data);

  if (!prompts || prompts.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mt: 2,
        background: alpha(theme.palette.primary.main, 0.05),
        border: `1px dashed ${theme.palette.primary.main}`,
        borderRadius: 2
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <SparkleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight="bold" color="primary">
          AI Quick Actions
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {prompts.map((prompt, index) => (
          <Tooltip key={index} title={prompt.tooltip || ''} arrow>
            <Chip
              icon={<IdeaIcon />}
              label={prompt.label}
              onClick={() => !disabled && onPromptClick(prompt)}
              disabled={disabled}
              clickable
              color="primary"
              variant="outlined"
              sx={{
                mb: 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.1),
                  transform: disabled ? 'none' : 'translateY(-2px)',
                  boxShadow: disabled ? 'none' : 2
                },
                transition: 'all 0.2s'
              }}
            />
          </Tooltip>
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Click any suggestion to get AI-powered help
      </Typography>
    </Paper>
  );
};

/**
 * Get prompts for specific wizard step
 */
function getPromptsForStep(step, data) {
  switch (step) {
    case 0: // Welcome
      return [
        {
          label: 'What type should I choose?',
          action: 'help',
          question: 'What type of business should I choose for my industry?',
          tooltip: 'Get help choosing the right business type'
        },
        {
          label: 'Solo vs Team?',
          action: 'help',
          question: "What's the difference between solo and team mode?",
          tooltip: 'Understand the differences'
        }
      ];

    case 1: // Business Profile
      return [
        {
          label: 'Suggest Business Name',
          action: 'businessName',
          params: { industry: data.industry || 'professional', style: 'professional' },
          tooltip: 'AI will suggest creative business names'
        },
        {
          label: 'Modern Name Ideas',
          action: 'businessName',
          params: { industry: data.industry || 'professional', style: 'modern' },
          tooltip: 'Get modern, trendy name suggestions'
        },
        {
          label: 'Creative Names',
          action: 'businessName',
          params: { industry: data.industry || 'professional', style: 'creative' },
          tooltip: 'Get unique, creative name ideas'
        }
      ];

    case 2: // Service
      return [
        {
          label: 'Suggest Services',
          action: 'services',
          params: {
            industry: data.industry || 'professional',
            businessType: data.businessType || 'solo'
          },
          tooltip: 'AI will suggest typical services for your industry'
        },
        {
          label: 'Suggest Pricing',
          action: 'pricing',
          params: {
            serviceName: data.serviceName || 'Service',
            industry: data.industry || 'professional'
          },
          tooltip: 'Get market-rate pricing suggestions'
        },
        {
          label: 'Write Description',
          action: 'description',
          params: {
            serviceName: data.serviceName || 'Service',
            industry: data.industry || 'professional',
            duration: data.serviceDuration || 60,
            price: data.servicePrice || 100
          },
          tooltip: 'Generate a compelling service description'
        }
      ];

    case 3: // Availability
      return [
        {
          label: 'Suggest Hours',
          action: 'availability',
          params: {
            industry: data.industry || 'professional',
            businessType: data.businessType || 'solo'
          },
          tooltip: 'Get optimal working hours for your industry'
        },
        {
          label: 'Weekend Hours?',
          action: 'help',
          question: `Should a ${data.industry || 'professional'} business be open on weekends?`,
          tooltip: 'Get advice on weekend availability'
        }
      ];

    default:
      return [];
  }
}

export default AIAssistantPrompts;
